import { supabase } from '../supabase';
import { Member } from '@/types/member';
import { Post } from '@/types/post';

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;

async function fetchWithRetry<T>(
  operation: () => Promise<T>,
  retries = MAX_RETRIES
): Promise<T> {
  try {
    return await operation();
  } catch (error) {
    if (
      retries > 0 &&
      error instanceof Error &&
      error.message.includes('Failed to fetch')
    ) {
      console.log(`Network error, retrying... (${retries} attempts remaining)`);
      await new Promise((r) => setTimeout(r, RETRY_DELAY));
      return fetchWithRetry(operation, retries - 1);
    }
    throw error;
  }
}

export async function search(
  query: string
): Promise<{ players: Member[]; posts: Post[]; clubs: any[] }> {
  try {
    console.log('📡 Performing search query:', query);

    const sanitizedQuery = query
      .replace(/['"]/g, '')
      .replace(/[^\w\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    // Resolve internal user ID from auth ID
    const {
      data: { user },
      error: authError,
    } = await supabase.auth.getUser();
    if (authError) throw authError;

    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('id')
      .eq('auth_id', user.id)
      .single();
    if (userError) throw userError;

    const loggedInUserId = userData.id;

    const { data, error } = await fetchWithRetry(() =>
      supabase.rpc('search_global', {
        query: sanitizedQuery,
        logged_in_user_id: loggedInUserId,
      })
    );
    if (error) throw error;

// ───────────────────────────────────────────────────────────
// 2. TEMPORARY debug line — prints every club row exactly as it
//    reaches the client.  Remove it once you’ve located the issue.
console.table(
  data
    .filter((i: any) => i.type === 'club')
    .map((c: any) => ({
      id:           c.id,
      name:         c.name,
      club_name:    c.club_name,
      display_name: c.display_name,
      logo:         c.avatar_url,
    }))
);

    
    const players: Member[] = data
  .filter((i: any) => i.type === 'user' && i.user_id !== loggedInUserId)
  .map((u: any) => ({
    id: u.user_id,
    name: u.name,
    city: u.city ?? '',
    jobTitle: u.job_type ?? '',
    company: u.company ?? '',
    image:
      u.avatar_url ||
      'https://…/gold_symbol-removebg.png',
    sports: u.sports ?? [],
connectionCount:
  u.total_connections && u.connections?.includes('a515d5df-8a26-409f-98e3-ea5e70c223f1')
    ? u.total_connections - 1
    : u.total_connections ?? undefined,
     role: u.role ?? 'player',

  }));


    const posts: Post[] = data
      .filter((i: any) => i.type === 'post')
      .map((p: any) => ({
        id: p.id,
        user_id: p.user_id,
        name: p.name,
        content: p.content,
        author: {
          id: p.user_id,
          name: p.author_name || 'Unknown',
          image: p.author_avatar || 'https://…/gold_symbol-removebg.png',
        },
        created_at: p.created_at,
        actionUrl: `/dashboard?post=${p.id}`,
      }));

  // src/lib/supabase/search.ts  (inside search())

const clubs = data
  .filter((i: any) => i.type === 'club')
  .map((c: any) => {
    const raw =
      c.name ??
      c.club_name ??          // ← Supabase rows use this
      c.display_name ??       // ← some legacy rows use this
      '';

    return {
      id:   c.id,
      name: raw.trim() || 'Unnamed club',
      city: c.city ?? '',
      logo: c.avatar_url ?? null,
      sports: c.sports ?? [],
    };
  });




    return { players, posts, clubs };
  } catch (e) {
    console.error('❌ Search API error:', e);
    return { players: [], posts: [], clubs: [] };
  }
}

type SuggestionResults = {
  popularMembers: { id: string; name: string; image: string; city: string }[];
  cities: string[];
  sports: { name: string; emoji: string }[];
  popularClubs: { id: string; name: string; logo: string }[];
  topSports: { id: string; name: string; emoji: string; count: number }[];
  popularActivities: { name: string; count: number }[];
  industries: string[];
  trendingPosts: {
    id: string;
    content: string;
    author: { id: string; name: string; image: string };
    createdAt: string;
  }[];
  popularCompanies: string[];
};

export async function getSearchSuggestions(): Promise<SuggestionResults> {
  try {
    const {
      data: { user },
      error: authError,
    } = await supabase.auth.getUser();
    if (authError) throw authError;

// Fetch most connected users
const { data: mostConnected, error: connError } = await supabase.rpc(
  'get_most_connected_users',
  { limit_count: 5 }
);
if (connError) throw connError;

// Extract user IDs and fetch their profiles
const ids = mostConnected.map((u: any) => u.user_id);
const { data: profiles = [], error: profilesError } = await supabase
  .from('users')
  .select('auth_id, id, full_name, avatar_url, city')
  .in('id', ids);
if (profilesError) throw profilesError;

// Maintain original order by mapping from RPC result
const idToProfile = Object.fromEntries(profiles.map((p) => [p.id, p]));
const sortedPopularMembers = mostConnected
  .map((entry) => {
    const profile = idToProfile[entry.user_id];
    if (!profile) return null;
    return {
      id: profile.auth_id,
      name: profile.full_name,
      image: profile.avatar_url || 'https://…/gold_symbol-removebg.png',
      city: profile.city,
      connectionCount: entry.total_connections,
    };
  })
  .filter(Boolean);

    
    const { data: citiesData = [] } = await supabase
      .from('user_counts_by_city')
      .select('city')
      .order('count', { ascending: false })
      .limit(10);
    const cities = citiesData.map((c) => c.city);

    const { data: sports = [] } = await supabase
      .from('sports')
      .select('name, emoji')
      .limit(10);

    const { data: popularClubs = [] } = await supabase.rpc('top_clubs_by_member_count', {
      limit_count: 5,
    });

    const { data: popularSports = [] } = await supabase.rpc('top_sports_by_popularity', {
      limit_count: 10,
    });
    const topSports = popularSports.map((s: any) => ({
      id: s.sport_id,
      name: s.sport_name,
      emoji: s.emoji,
      count: s.member_count,
    }));

    const { data: popularActivitiesData = [] } = await supabase
      .from('activity_counts_by_sport')
      .select('sport, count')
      .order('count', { ascending: false })
      .limit(10);
    const popularActivities = popularActivitiesData.map((a: any) => ({
      name: a.sport,
      count: a.count,
    }));

    const { data: trendingPosts = [] } = await supabase
      .from('posts')
      .select(`
        id,
        content,
        created_at,
        likes_count,
        users (
          auth_id,
          full_name,
          avatar_url
        )
      `)
      .order('likes_count', { ascending: false })
      .limit(5);
    const trending = trendingPosts.map((p: any) => ({
      id: p.id,
      content: p.content,
      author: {
        id: p.users.auth_id,
        name: p.users.full_name,
        image: p.users.avatar_url,
      },
      createdAt: p.created_at,
    }));

    const { data: industriesRaw = [] } = await supabase
      .from('user_profiles')
      .select('preferences->industry')
      .not('preferences->industry', 'is', null)
      .limit(10);
    const industries = [
      ...new Set(industriesRaw.map((i: any) => i.preferences.industry)),
    ];

    const { data: companiesData = [] } = await supabase
      .from('users')
      .select('company')
      .not('company', 'is', null)
      .limit(100);
    const companyCounts: Record<string, number> = {};
    companiesData.forEach(({ company }) => {
      if (company) companyCounts[company] = (companyCounts[company] || 0) + 1;
    });
    const popularCompanies = Object.entries(companyCounts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 10)
      .map(([company]) => company);

    return {
     popularMembers: sortedPopularMembers,

      cities,
      sports: sports.map((s) => ({ name: s.name, emoji: s.emoji })),
      popularClubs: popularClubs.map((c: any) => ({
        id: c.club_id,
        name: c.club_name,
        logo:
          c.logo ||
          'https://…/default-club-logo.png',
      })),
      topSports,
      popularActivities,
      industries,
      trendingPosts: trending,
      popularCompanies,
    };
  } catch (error) {
    console.error('Error fetching search suggestions:', error);
    return {
      popularMembers: [],
      cities: [],
      sports: [],
      popularClubs: [],
      topSports: [],
      popularActivities: [],
      industries: [],
      trendingPosts: [],
      popularCompanies: [],
    };
  }
}
